<script setup>
import { nextTick, ref, watch } from 'vue';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isSending: {
    type: Boolean,
    default: false,
  },
  botName: {
    type: String,
    default: '',
  },
  botAvatar: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['send']);

const STARTER_KEYS = ['GREETING', 'HOURS', 'HUMAN'];

const draft = ref('');
const pending = ref('');
const listRef = ref(null);
// The store keeps no timestamps, so stamp each message when it first shows up.
const stamps = ref([]);

const formatTime = date =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const scrollToBottom = async () => {
  await nextTick();
  listRef.value?.scrollTo?.({ top: listRef.value.scrollHeight });
};

watch(
  () => props.messages.length,
  length => {
    const now = formatTime(new Date());
    stamps.value =
      length < stamps.value.length
        ? []
        : [
            ...stamps.value,
            ...Array(length - stamps.value.length).fill(now),
          ];
    scrollToBottom();
  },
  { immediate: true }
);

watch(
  () => props.isSending,
  sending => {
    if (!sending) pending.value = '';
    scrollToBottom();
  }
);

const sendContent = content => {
  if (!content || props.isSending) return false;
  pending.value = content;
  emit('send', content);
  return true;
};

const submit = () => {
  if (sendContent(draft.value.trim())) draft.value = '';
};

const starterLabel = key => `AI_AGENT.PLAYGROUND.STARTERS.${key}`;
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <div
      ref="listRef"
      class="flex flex-col flex-1 gap-3 px-4 py-4 overflow-y-auto min-h-64"
      aria-live="polite"
    >
      <div
        v-if="!messages.length && !isSending"
        class="flex flex-col items-center justify-center flex-1 gap-4 text-center"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-12 bg-n-brand/10 text-n-blue-11 ring-4 ring-n-brand/5"
        >
          <span class="i-lucide-message-circle-dashed size-6" />
        </span>
        <p class="max-w-xs text-body-main text-n-slate-11">
          {{ $t('AI_AGENT.PLAYGROUND.EMPTY') }}
        </p>
        <div class="flex flex-wrap justify-center gap-2">
          <button
            v-for="key in STARTER_KEYS"
            :key="key"
            type="button"
            class="px-3 py-1.5 transition-colors rounded-full text-label-small bg-n-alpha-2 text-n-slate-12 hover:bg-n-brand/10 hover:text-n-blue-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-reduce:transition-none"
            :data-testid="`playground-starter-${key}`"
            @click="sendContent($t(starterLabel(key)))"
          >
            {{ $t(starterLabel(key)) }}
          </button>
        </div>
      </div>

      <div
        v-for="(message, index) in messages"
        :key="index"
        class="flex items-end gap-2"
        :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <Avatar
          v-if="message.role !== 'user'"
          :name="botName"
          :src="botAvatar"
          :size="24"
          rounded-full
          class="mb-5 shrink-0"
        />
        <div
          class="flex flex-col max-w-[80%] gap-1"
          :class="message.role === 'user' ? 'items-end' : 'items-start'"
        >
          <div
            class="px-3.5 py-2 whitespace-pre-wrap break-words rounded-2xl text-body-main"
            :class="
              message.role === 'user'
                ? 'bg-n-brand text-white rounded-br-md'
                : 'bg-n-alpha-2 text-n-slate-12 rounded-bl-md outline outline-1 -outline-offset-1 outline-n-weak'
            "
            :data-testid="`playground-message-${message.role}`"
          >
            {{ message.content }}
          </div>
          <span class="px-1 text-label-small text-n-slate-10">
            {{ stamps[index] }}
          </span>
        </div>
      </div>

      <template v-if="isSending">
        <div v-if="pending" class="flex justify-end">
          <div
            class="max-w-[80%] px-3.5 py-2 whitespace-pre-wrap break-words rounded-2xl rounded-br-md text-body-main bg-n-brand text-white opacity-70"
            data-testid="playground-message-pending"
          >
            {{ pending }}
          </div>
        </div>
        <div class="flex items-end gap-2" data-testid="playground-typing">
          <Avatar
            :name="botName"
            :src="botAvatar"
            :size="24"
            rounded-full
            class="shrink-0"
          />
          <div
            class="flex items-center gap-1 px-3.5 py-3 rounded-2xl rounded-bl-md bg-n-alpha-2 outline outline-1 -outline-offset-1 outline-n-weak"
            :aria-label="$t('AI_AGENT.PLAYGROUND.WAITING')"
          >
            <span
              class="rounded-full size-1.5 bg-n-slate-10 animate-bounce motion-reduce:animate-none"
            />
            <span
              class="rounded-full size-1.5 bg-n-slate-10 animate-bounce [animation-delay:150ms] motion-reduce:animate-none"
            />
            <span
              class="rounded-full size-1.5 bg-n-slate-10 animate-bounce [animation-delay:300ms] motion-reduce:animate-none"
            />
          </div>
        </div>
      </template>
    </div>
    <form
      class="flex items-center gap-2 p-3 border-t border-n-weak"
      @submit.prevent="submit"
    >
      <div class="flex-1 min-w-0">
        <Input
          v-model="draft"
          :placeholder="$t('AI_AGENT.PLAYGROUND.INPUT_PLACEHOLDER')"
          :disabled="isSending"
        />
      </div>
      <button
        type="submit"
        class="flex items-center justify-center transition rounded-lg shrink-0 size-9 bg-n-brand text-white hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 disabled:opacity-40 disabled:hover:brightness-100 motion-reduce:transition-none"
        :disabled="isSending || !draft.trim()"
        :aria-label="$t('AI_AGENT.PLAYGROUND.SEND')"
      >
        <span class="i-lucide-send-horizontal size-4" />
      </button>
    </form>
  </div>
</template>
